/**
 * 🏢 Collaborator Dashboard — /my-company-space (Vague 4 P1)
 * ===========================================================
 *
 * Espace focalisé pour le collaborateur entreprise :
 *   - 1 jauge par commande active (pas d'agrégation ambiguë)
 *   - Validité temporelle avec alerte urgente si ≤ 7j
 *   - CTA "Réserver une session" → redirige marketplace
 *   - Liste "Mes coachs autorisés" (source = marketplace B2B filtrée)
 *
 * Source de vérité : `useEnterpriseContext()` (issu de /api/me/enterprise-context).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useEnterpriseContext } from '../contexts/EnterpriseContext';
import { getApiUrl } from '../config/environment';
import EnterpriseQuotaGauge from '../components/EnterpriseQuotaGauge';
import BackButton from '../components/BackButton';
import { Loader2, Building2, ArrowRight, Gift, CheckCircle2, FileText } from 'lucide-react';

const API = getApiUrl();

export default function CollaboratorDashboard() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { isCollaborator, orders, totalSessionsRemaining, loading } = useEnterpriseContext();
  const [coaches, setCoaches] = useState([]);
  const [coachesLoading, setCoachesLoading] = useState(true);
  // P1 Iter237 — Widget COACHDIA Prism offerts
  const [insightsCtx, setInsightsCtx] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(true);
  // 🧭 Sprint 3 Iter243 — Modale de décision "déjà testé"
  const [prismDecision, setPrismDecision] = useState(null);
  const [decisionBusy, setDecisionBusy] = useState(false);
  // 🔒 Iter251 — Source de vérité test Discovery (pour CTA adaptatif)
  //   status machine : not_started | in_progress | awaiting_ai | completed_unpaid | paid
  const [discoveryStatus, setDiscoveryStatus] = useState(null);

  const fetchDiscoveryStatus = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/discovery/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { setDiscoveryStatus(null); return; }
      setDiscoveryStatus(await res.json());
    } catch { setDiscoveryStatus(null); }
  }, [token]);

  const fetchInsightsContext = useCallback(async () => {
    if (!token) return;
    setInsightsLoading(true);
    try {
      const res = await fetch(`${API}/me/insights-context`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      setInsightsCtx(d);
    } catch { setInsightsCtx(null); }
    finally { setInsightsLoading(false); }
  }, [token]);

  const fetchPrismDecision = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/me/prism-decision-context`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      setPrismDecision(d);
    } catch { setPrismDecision(null); }
  }, [token]);

  const submitDecision = useCallback(async (choice) => {
    if (!token) return;
    setDecisionBusy(true);
    try {
      const res = await fetch(`${API}/me/prism-decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ choice }),
      });
      if (!res.ok) throw new Error((await res.json())?.detail || 'Échec');
      // Refresh both contexts
      await Promise.all([fetchInsightsContext(), fetchPrismDecision()]);
      if (choice === 'retake') {
        navigate('/discovery');
      }
    } catch (e) {
      alert(typeof e === 'string' ? e : (e.message || 'Échec'));
    } finally { setDecisionBusy(false); }
  }, [token, fetchInsightsContext, fetchPrismDecision, navigate]);

  useEffect(() => { fetchInsightsContext(); fetchPrismDecision(); fetchDiscoveryStatus(); },
    [fetchInsightsContext, fetchPrismDecision, fetchDiscoveryStatus]);

  const fetchMyCoaches = useCallback(async () => {
    if (!token) return;
    setCoachesLoading(true);
    try {
      const res = await fetch(`${API}/marketplace/coaches?limit=24`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      setCoaches(d.coaches || []);
    } catch { setCoaches([]); }
    finally { setCoachesLoading(false); }
  }, [token]);

  useEffect(() => { fetchMyCoaches(); }, [fetchMyCoaches]);

  const ordersByType = useMemo(() => {
    const by = { human: [], ia: [] };
    for (const o of orders) {
      (by[o.type] || by.ia).push(o);
    }
    return by;
  }, [orders]);

  if (loading) {
    return (
      <div className="theme-light min-h-[50vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#92710A]" />
      </div>
    );
  }

  if (!isCollaborator) {
    return (
      <div className="theme-light">
        <div className="max-w-3xl mx-auto px-4 pt-24 pb-[max(6rem,env(safe-area-inset-bottom))]" data-testid="no-enterprise-notice">
          <BackButton />
          <div className="mt-6 lt-card p-8 text-center">
            <Building2 className="w-10 h-10 mx-auto text-[#92710A] mb-3" />
            <h1 className="font-display text-2xl text-[#111827] mb-2">Aucun forfait entreprise actif</h1>
            <p className="text-[14px] text-[#4B5563] mb-6">
              Vous n’êtes actuellement rattaché à aucun forfait entreprise. Contactez votre service RH
              si vous pensez qu’il s’agit d’une erreur.
            </p>
            <button onClick={() => navigate('/marketplace')}
              className="px-5 py-2.5 rounded-full bg-[#111827] text-white text-sm font-semibold hover:bg-[#1F2937] inline-flex items-center gap-2">
              Découvrir la marketplace <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="theme-light" data-testid="collaborator-dashboard">
      {/* 🔒 Iter252 — Bannière B2B verte retirée (doublon avec le hero premium ci-dessous) */}

      {/* 🧭 Sprint 3 Iter243 — Modale décision collab déjà testé */}
      {prismDecision?.decision_required && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="prism-decision-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] mb-2">
              Invitation · {prismDecision.sponsor_company_name}
            </div>
            <h2 className="font-display text-xl text-[#111827] mb-3">
              Vous avez déjà réalisé COACHDIA Prism
            </h2>
            <p className="text-sm text-[#4B5563] mb-5 leading-relaxed">
              <strong>{prismDecision.sponsor_company_name}</strong> vous offre un test COACHDIA Prism.
              Vous pouvez soit <strong>refaire le test</strong> pour obtenir un profil actualisé,
              soit <strong>réutiliser votre rapport existant</strong>.
            </p>
            <div className="space-y-2">
              <button onClick={() => submitDecision('retake')} disabled={decisionBusy}
                      data-testid="prism-decision-retake"
                      className="w-full text-left px-4 py-3 rounded-lg border border-[#92710A] hover:bg-[#FBF8F1] disabled:opacity-50">
                <div className="text-sm font-medium text-[#92710A]">Refaire le test (offert)</div>
                <div className="text-[11px] text-[#6B7280] mt-0.5">Nouveau profil actualisé, 15 min</div>
              </button>
              <button onClick={() => submitDecision('reuse')} disabled={decisionBusy}
                      data-testid="prism-decision-reuse"
                      className="w-full text-left px-4 py-3 rounded-lg border border-stone-200 hover:bg-stone-50 disabled:opacity-50">
                <div className="text-sm font-medium text-[#111827]">Réutiliser mon rapport existant</div>
                <div className="text-[11px] text-[#6B7280] mt-0.5">
                  Rapport du {prismDecision.existing_completed_at
                    ? new Date(prismDecision.existing_completed_at).toLocaleDateString('fr-FR')
                    : '—'}
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 pt-20 sm:pt-24 pb-[max(6rem,env(safe-area-inset-bottom))] sm:pb-16">
        <BackButton />

        {/* 🎨 Iter252 — Header premium éditorial (refonte haut de gamme) */}
        <motion.header
          initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mt-8 mb-12 relative"
        >
          {/* Ligne or discrète (signature éditoriale) */}
          <div className="w-12 h-[2px] bg-gradient-to-r from-[#92710A] to-[#D4AF37] mb-5" aria-hidden="true" />

          {/* Eyebrow premium — tracking étalé, uppercase sobre */}
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#92710A] font-semibold mb-4">
            <Building2 className="w-3.5 h-3.5" />
            Espace entreprise
            {insightsCtx?.company_name && (
              <>
                <span className="text-[#D4AF37]">·</span>
                <span className="text-[#3D2F1C] font-medium normal-case tracking-normal">
                  {insightsCtx.company_name}
                </span>
              </>
            )}
          </div>

          {/* Titre éditorial (serif, large, respiration) */}
          <h1 className="font-display text-3xl sm:text-4xl lg:text-[56px] text-[#1F1A12] leading-[1.05] tracking-tight max-w-3xl">
            Votre accompagnement,
            <span className="block text-[#92710A] font-normal italic">orchestré.</span>
          </h1>

          {/* Meta-ligne premium sur une seule ligne avec séparateurs sobres */}
          <div className="mt-8 pt-6 border-t border-[#E8DFC8] grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="flex items-baseline gap-3">
              <span className="font-display text-4xl text-[#1F1A12] leading-none tabular-nums">{totalSessionsRemaining}</span>
              <span className="text-[13px] text-[#6B5A3E] leading-tight">
                Session{totalSessionsRemaining > 1 ? 's' : ''}<br />
                <span className="text-[11px] uppercase tracking-wider text-[#92710A] font-semibold">à utiliser</span>
              </span>
            </div>
            {orders.length >= 1 && (
              <div className="flex items-baseline gap-3 sm:border-l sm:border-[#E8DFC8] sm:pl-6">
                <span className="font-display text-4xl text-[#1F1A12] leading-none tabular-nums">{orders.length}</span>
                <span className="text-[13px] text-[#6B5A3E] leading-tight">
                  Forfait{orders.length > 1 ? 's' : ''}<br />
                  <span className="text-[11px] uppercase tracking-wider text-[#92710A] font-semibold">
                    {orders.length > 1 ? 'actifs' : 'actif'}
                  </span>
                </span>
              </div>
            )}
            {(() => {
              const nextExpiry = orders
                .map(o => o.validity_until || o.expires_at || null)
                .filter(Boolean).sort()[0];
              if (!nextExpiry) return null;
              const date = new Date(nextExpiry);
              const day = date.toLocaleDateString('fr-FR', { day: '2-digit' });
              const monthYear = date.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
              return (
                <div className="flex items-baseline gap-3 sm:border-l sm:border-[#E8DFC8] sm:pl-6"
                     data-testid="collaborator-dashboard-next-expiry">
                  <span className="font-display text-4xl text-[#1F1A12] leading-none tabular-nums">{day}</span>
                  <span className="text-[13px] text-[#6B5A3E] leading-tight">
                    {monthYear}<br />
                    <span className="text-[11px] uppercase tracking-wider text-[#92710A] font-semibold">
                      Valide jusqu'au
                    </span>
                  </span>
                </div>
              );
            })()}
          </div>
        </motion.header>

        {/* P1 Iter237 — Widget COACHDIA Prism offerts (bannière prominent avant les jauges) */}
        {!insightsLoading && insightsCtx && (insightsCtx.sponsored_available || insightsCtx.sponsored_consumed) && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="mb-8"
            data-testid="insights-sponsored-widget">
            {insightsCtx.sponsored_available ? (
              (() => {
                // 🔒 Iter251 — CTA piloté par state machine backend /discovery/status
                //   not_started                        → "Commencer mon test" → /discovery
                //   in_progress | awaiting_ai | completed_unpaid → "Continuer mon test" → /discovery
                //   paid (rapport disponible)          → "Accéder au rapport" → /my-discovery
                const s = discoveryStatus?.status || 'not_started';
                const isPaid = s === 'paid';
                const isInProgress = ['in_progress', 'awaiting_ai', 'completed_unpaid'].includes(s);
                const ctaLabel = isPaid
                  ? 'Accéder au rapport'
                  : isInProgress ? 'Continuer mon test' : 'Commencer mon test';
                const ctaTarget = isPaid ? '/my-discovery' : '/discovery';
                const ctaTestId = isPaid
                  ? 'insights-sponsored-open-report-btn'
                  : isInProgress ? 'insights-sponsored-continue-test-btn' : 'insights-sponsored-start-btn';
                const description = isPaid
                  ? 'Consultez votre rapport PDF premium, votre cartographie d\'énergies et vos coachs recommandés.'
                  : isInProgress
                    ? 'Votre test est en cours. Reprenez là où vous vous êtes arrêté — il vous reste quelques questions.'
                    : 'Rapport PDF premium, cartographie de vos énergies, plan 30/60/90 jours et recommandation de coachs — 100% confidentiel, 15 min.';
                return (
                  <div className="rounded-2xl overflow-hidden shadow-lg border border-[#92710A]/30">
                    <div className="bg-gradient-to-br from-[#2A2319] via-[#3D2F1C] to-[#4A3620] p-6 text-white">
                      <div className="flex items-start gap-4 flex-wrap">
                        <div className="w-12 h-12 rounded-full bg-[#F3D060]/20 border border-[#F3D060]/50 flex items-center justify-center shrink-0">
                          <Gift className="w-6 h-6 text-[#F3D060]" />
                        </div>
                        <div className="flex-1 min-w-[200px]">
                          <div className="text-[10px] tracking-[0.3em] uppercase text-[#F3D060] mb-1">Cadeau entreprise</div>
                          <h2 className="font-display text-xl sm:text-2xl leading-tight">
                            {isPaid
                              ? <>Votre profil COACHDIA Prism est disponible{insightsCtx.company_name ? <> · offert par <span className="text-[#F3D060]">{insightsCtx.company_name}</span></> : null}</>
                              : <>Votre test COACHDIA Prism est offert{insightsCtx.company_name ? <> par <span className="text-[#F3D060]">{insightsCtx.company_name}</span></> : null}</>}
                          </h2>
                          <p className="text-[#E8DCB8] text-sm mt-2 max-w-xl">{description}</p>
                        </div>
                        <button
                          onClick={() => navigate(ctaTarget, { state: { from: '/my-company-space' } })}
                          data-testid={ctaTestId}
                          className="px-5 py-3 rounded-full bg-[#F3D060] text-[#2A2319] text-sm font-semibold hover:bg-[#E9C340] inline-flex items-center gap-2 shrink-0"
                        >
                          {isPaid && <FileText className="w-4 h-4" />}
                          {ctaLabel} <ArrowRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()
            ) : (
              <div className="rounded-2xl p-5 bg-gradient-to-br from-[#FBF8F1] to-[#F5EFE1] border border-[#92710A]/30 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3 flex-1 min-w-[200px]">
                  <CheckCircle2 className="w-5 h-5 text-[#047857] shrink-0 mt-0.5" />
                  <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] mb-0.5">Test COACHDIA Prism consommé</div>
                    <div className="font-display text-sm text-stone-900">
                      Vous avez utilisé votre test offert{insightsCtx.company_name ? <> par <strong>{insightsCtx.company_name}</strong></> : null}.
                    </div>
                    <div className="text-xs text-stone-600 mt-1">Consultez votre parcours personnalisé et les coachs recommandés.</div>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/my-growth-path')}
                  data-testid="insights-sponsored-view-path-btn"
                  className="px-4 py-2 rounded-full bg-[#1F2937] text-white text-sm font-medium hover:bg-[#111827] inline-flex items-center gap-2 shrink-0"
                >
                  Voir mon parcours <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </motion.div>
        )}

        {/* Gauges — 1 par commande */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10" data-testid="orders-gauges">
          {orders.map((o, idx) => (
            <motion.div key={o.order_id}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.08 }}>
              <EnterpriseQuotaGauge
                allocated={o.sessions_allocated}
                used={o.sessions_used}
                remaining={o.sessions_remaining}
                validUntil={o.valid_until}
                companyName={o.company_name}
                orderType={o.type}
                eligibleCoachCount={(o.eligible_coach_ids || []).length}
                onBook={() => navigate('/marketplace')}
              />
            </motion.div>
          ))}
        </section>

        {/* Mes coachs autorisés */}
        <section data-testid="eligible-coaches-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl text-[#111827]">Mes coachs autorisés</h2>
            <button onClick={() => navigate('/marketplace')}
              data-testid="collab-dashboard-browse-all"
              className="text-[12px] text-[#92710A] font-semibold hover:underline inline-flex items-center gap-1">
              Voir tous <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
          {coachesLoading ? (
            <div className="py-8 text-center text-[13px] text-[#6B7280]">
              <Loader2 className="w-4 h-4 animate-spin mx-auto mb-2" /> Chargement…
            </div>
          ) : coaches.length === 0 ? (
            <div className="lt-card p-8 text-center text-[13px] text-[#6B7280]"
              data-testid="eligible-coaches-empty">
              Aucun coach n’est encore disponible pour vos forfaits.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {coaches.slice(0, 6).map((c, i) => (
                <button key={c.user_id}
                  onClick={() => navigate(`/marketplace/coach/${c.user_id}`)}
                  data-testid={`collab-coach-card-${c.user_id}`}
                  className="text-left lt-card p-4 hover:border-[#92710A] hover:shadow-md transition-all">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#92710A] to-[#B8860B] flex items-center justify-center text-white font-semibold">
                      {(c.display_name || '?').slice(0, 1).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[#111827] text-[14px] truncate">
                        {c.display_name || 'Coach'}
                      </div>
                      <div className="text-[11px] text-[#6B7280] truncate">
                        {(c.specialties || []).slice(0, 2).join(' · ') || '—'}
                      </div>
                      <div className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#047857] bg-[#D1FAE5] border border-[#A7F3D0] px-1.5 py-0.5 rounded-full font-semibold">
                        Inclus
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
